import { isWarningOffer, VendorOffer, VendorOfferMap } from '../../shared/models/cart.models';
import { inCents, SplitOptionList, SplitPlan } from '../../shared/services/cart-split-calculator';

// Pure selection for the cart rail: no DOM, no signals, no DI. Given the shown
// split options and the per-vendor totals, returns the rows the rail draws.

/** PRD D-16: the rail's row count, the maximum it showed before the split. */
export const RAIL_MAX_ROWS = 5;

export type RailRow =
  | { readonly kind: 'option'; readonly plan: SplitPlan }
  | { readonly kind: 'warning'; readonly offer: VendorOffer };

/**
 * PRD AC-22 (amended 2026-09-08): every single-store shown option and the best
 * option of each multi-store shown list, merged by ascending total in cents,
 * then, in the slots left, the warning vendors in the map's order.
 */
export function selectRailRows(
  lists: readonly SplitOptionList[],
  offers: VendorOfferMap,
  max: number = RAIL_MAX_ROWS
): RailRow[] {
  // The lists come in ascending store-count order and each is already cheapest
  // first, and the sort is stable, so a tie in cents keeps the option with the
  // fewer stores and then its own list's order.
  const options: RailRow[] = lists
    // D-16 as amended: a multi-store list contributes its best option only, so
    // one cart cannot fill all five rows with pairs and leave no single store to
    // compare them against. The single-store list is the pharmacy list itself
    // and keeps every option, as the rail listed every complete vendor before.
    .flatMap((list) => (list.storeCount > 1 ? list.options.slice(0, 1) : list.options))
    .sort((a, b) => inCents(a.total) - inCents(b.total))
    .map((plan) => ({ kind: 'option', plan }));

  // The same predicate the cart page's warning slice uses, so the two count the
  // same rows.
  const warnings: RailRow[] = Array.from(offers.values())
    .filter(isWarningOffer)
    .map((offer) => ({ kind: 'warning', offer }));

  return [...options, ...warnings].slice(0, max);
}
